import {useRef, useState} from 'react'
import {faSearch} from '@fortawesome/free-solid-svg-icons'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import debounce from 'lodash/debounce'
import {mthen} from '@freckle/maybe'
import {useSafeEffectExtraDeps, useSafeEffect} from '@freckle/react-hooks'
import {Input} from '@freckle/educator-entities/ts/common/components/base/input'
import {filterInputWrapper, filterInputIcon, filterInput} from './filter-input.module.scss'

type Props = {
  initValue: string
  routeFn: (value: string) => void
  dataTest?: string
  parseInput?: (value: string) => string
  validateInput?: (value: string) => boolean
  onlyValidateOnEnter?: boolean
}

export function FilterInput(props: Props) {
  const {
    initValue,
    routeFn,
    dataTest,
    onlyValidateOnEnter = false,
    parseInput = (s: string) => s,
    validateInput = (_s: string) => true,
  } = props
  const [inputValue, setInputValue] = useState(initValue)
  const inputRef = useRef<HTMLElement | null>(null)

  const onEnter = () => {
    if (validateInput(inputValue)) {
      routeFn(parseInput(inputValue))
    } else {
      setInputValue('')
    }
  }
  const debouncedEnter = debounce(onEnter, 500)

  const [pendingChangeByOnChange, updateChangeStatus] = useState(false)
  const onChange = (s: string) => {
    setInputValue(s)
    updateChangeStatus(true)
  }
  useSafeEffectExtraDeps(
    ({debouncedEnter}: {debouncedEnter: () => void}) => {
      if (!onlyValidateOnEnter && pendingChangeByOnChange) {
        debouncedEnter()
        updateChangeStatus(false)
      }
    },
    [pendingChangeByOnChange, onlyValidateOnEnter],
    {
      debouncedEnter: {
        value: debouncedEnter,
        comparator: () => true,
      },
    },
  )

  useSafeEffect(() => {
    if (initValue.length !== 0) {
      mthen(inputRef.current, inputRefCur => inputRefCur.focus())
    }
  }, [initValue.length])

  return (
    <div className={filterInputWrapper}>
      <FontAwesomeIcon icon={faSearch} className={filterInputIcon} size="lg" />
      <Input
        value={inputValue}
        onChange={onChange}
        onEnter={onEnter}
        className={filterInput}
        dataTest={dataTest}
        getRef={(ref: HTMLElement) => {
          inputRef.current = ref
        }}
      />
    </div>
  )
}
